import { useState, useEffect, useMemo, Key } from "react";
import { Table, Row, Col, Input, Tag, Button, Switch, Modal } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getClientJobs, updateClientJobStatus } from "@/ts/retrieve-data";
import {
    IClientJob,
    IClientListItem,
    ClientJobTableColumns,
    JobsData,
    ClientJobFormFields,
} from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import dayjs from "dayjs";
import { debounce } from "underscore";
import * as Sentry from "@sentry/react";
import { convertIsoDate, formatCronString } from "@/ts/utils";
import { FaHistory } from "react-icons/fa";
import ClientJobHistoryModal from "@/components/modals/ClientJobHistoryModal";
import { usePermissionCheck, useUserTimezone } from "@/hooks/AllCustomHooks";
import ClientJobForm from "./ClientJobForm";
import {
    DEFAULT_PAGE_SIZE,
    DEBOUNCE_DELAY,
    FIRST_PAGE,
    DEFAULT_PAGE_SIZE_OPTIONS,
    DateTimeFormats,
} from "@/ts/constants";

interface ClientJobsProps {
    client: IClientListItem;
}

const INITIAL_JOBS_DATA: JobsData = {
    loading: true,
    jobs: [],
    pagination: {
        current: FIRST_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    },
    searchQuery: "",
};

const ClientJobs: React.FC<ClientJobsProps> = ({ client }) => {
    const { canCreateClient, canUpdateClient } = usePermissionCheck();
    const [jobsData, setJobsData] = useState<JobsData>(INITIAL_JOBS_DATA);
    const [selectedJob, setSelectedJob] = useState<IClientJob | null>(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [addJobDisplay, setAddJobDisplay] = useState(false);
    const [editJobData, setEditJobData] = useState<IClientJob | null>(null);
    const currentTimeZone = useUserTimezone(client.timezones?.[0]);

    const columns: ColumnsType<IClientJob> = [
        {
            title: ClientJobTableColumns.ID,
            dataIndex: ClientJobFormFields.ID,
            key: ClientJobFormFields.ID,
            width: 80,
            sorter: (a: IClientJob, b: IClientJob) => a.id - b.id,
        },
        {
            title: ClientJobTableColumns.REPORT_NAME,
            dataIndex: ClientJobFormFields.REPORT_NAME,
            key: ClientJobFormFields.REPORT_NAME,
            render: (text: string | null) => text || "N/A",
            sorter: (a: IClientJob, b: IClientJob) => {
                const nameA = a.report_name || "";
                const nameB = b.report_name || "";
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: ClientJobTableColumns.STATUS,
            dataIndex: ClientJobFormFields.STATUS,
            key: ClientJobFormFields.STATUS,
            width: 100,
            filters: [
                { text: "Active", value: true },
                { text: "Inactive", value: false },
            ],
            onFilter: (value: Key | boolean, record: IClientJob) =>
                record.status === value,
            render: (status: boolean, record: IClientJob) => {
                const handleStatusChange = async (checked: boolean) => {
                    if (!canUpdateClient) {
                        showToast(
                            "danger",
                            "You do not have permission to update job status"
                        );
                        return;
                    }

                    try {
                        Modal.confirm({
                            title: "Confirm Status Change",
                            content: (
                                <div className="py-3 text-body">
                                    Are you sure you want to set this job as{" "}
                                    <span className="fw-bold">
                                        {checked ? "active" : "inactive"}
                                    </span>
                                    ?
                                </div>
                            ),
                            okText: "Yes",
                            cancelText: "No",
                            className: "status-confirmation-modal",
                            width: 400,
                            centered: true,
                            style: { padding: "16px" },
                            styles: { body: { padding: "16px" } },
                            onOk: async () => {
                                await updateClientJobStatus(
                                    client.id,
                                    record.id,
                                    checked
                                );
                                showToast(
                                    "success",
                                    "Job status updated successfully"
                                );
                                fetchJobs(
                                    jobsData.pagination.current,
                                    jobsData.pagination.pageSize,
                                    jobsData.searchQuery
                                );
                            },
                        });
                    } catch (error: any) {
                        showToast(
                            "danger",
                            error.message || "Failed to update job status"
                        );
                    }
                };

                return (
                    <Switch
                        checked={status}
                        onChange={handleStatusChange}
                        disabled={!canUpdateClient}
                    />
                );
            },
        },
        {
            title: ClientJobTableColumns.SOURCE,
            dataIndex: ClientJobFormFields.SOURCE,
            key: ClientJobFormFields.SOURCE,
            sorter: (a: IClientJob, b: IClientJob) =>
                a.source.localeCompare(b.source),
        },
        {
            title: ClientJobTableColumns.RECORD_TYPE,
            dataIndex: ClientJobFormFields.RECORD_TYPE,
            key: ClientJobFormFields.RECORD_TYPE,
            render: (text: string | null) => text || "N/A",
            sorter: (a: IClientJob, b: IClientJob) => {
                const typeA = a.record_type || "";
                const typeB = b.record_type || "";
                return typeA.localeCompare(typeB);
            },
        },
        {
            title: ClientJobTableColumns.SCHEDULE,
            dataIndex: ClientJobFormFields.CRON_STRING,
            key: ClientJobFormFields.CRON_STRING,
            sorter: (a: IClientJob, b: IClientJob) =>
                a.cron_string.localeCompare(b.cron_string),
            render: (cronString: string) => (
                <div
                    role="button"
                    onClick={(e) => {
                        const el = e.currentTarget;
                        el.textContent =
                            el.textContent === cronString
                                ? formatCronString(cronString)
                                : cronString;
                    }}
                >
                    {formatCronString(cronString)}
                </div>
            ),
        },
        {
            title: ClientJobTableColumns.SCHEDULE_TIMEZONE,
            dataIndex: ClientJobFormFields.SCHEDULE_TIMEZONE,
            key: ClientJobFormFields.SCHEDULE_TIMEZONE,
            render: (timezone: string | null) => timezone || "N/A",
            sorter: (a: IClientJob, b: IClientJob) => {
                const timezoneA = a.schedule_timezone || "";
                const timezoneB = b.schedule_timezone || "";
                return timezoneA.localeCompare(timezoneB);
            },
        },
        {
            title: ClientJobTableColumns.PERIOD,
            dataIndex: ClientJobFormFields.PERIOD,
            key: ClientJobFormFields.PERIOD,
            sorter: (a: IClientJob, b: IClientJob) =>
                a.period.localeCompare(b.period),
        },
        {
            title: ClientJobTableColumns.PRIORITY,
            dataIndex: ClientJobFormFields.JOB_PRIORITY,
            key: ClientJobFormFields.JOB_PRIORITY,
            filters: [
                { text: "low", value: "low" },
                { text: "high", value: "high" },
            ],
            onFilter: (value: Key | boolean, record: IClientJob) =>
                record.job_priority === value,
            sorter: (a: IClientJob, b: IClientJob) =>
                a.job_priority.localeCompare(b.job_priority),
            render: (priority: string) => (
                <Tag
                    color={
                        priority === "low"
                            ? "green"
                            : priority === "high"
                            ? "red"
                            : undefined
                    }
                >
                    <strong>{priority}</strong>
                </Tag>
            ),
        },
        {
            title: ClientJobTableColumns.CREATED_ON,
            dataIndex: ClientJobFormFields.CREATED_ON,
            key: ClientJobFormFields.CREATED_ON,
            render: (date: string) => (
                <div>
                    <div>{convertIsoDate(String(date), currentTimeZone)}</div>
                    <div className="small text-muted">
                        {convertIsoDate(String(date))}{" "}
                        {DateTimeFormats.UTC_SUFFIX}
                    </div>
                </div>
            ),
            sorter: (a: IClientJob, b: IClientJob) =>
                dayjs(a.created_on).unix() - dayjs(b.created_on).unix(),
        },
        {
            title: ClientJobTableColumns.MODIFIED_ON,
            dataIndex: ClientJobFormFields.MODIFIED_ON,
            key: ClientJobFormFields.MODIFIED_ON,
            render: (date: string) => (
                <div>
                    <div>{convertIsoDate(String(date), currentTimeZone)}</div>
                    <div className="small text-muted">
                        {convertIsoDate(String(date))}{" "}
                        {DateTimeFormats.UTC_SUFFIX}
                    </div>
                </div>
            ),
            sorter: (a: IClientJob, b: IClientJob) =>
                dayjs(a.modified_on).unix() - dayjs(b.modified_on).unix(),
        },
        {
            title: ClientJobTableColumns.HISTORY,
            dataIndex: ClientJobFormFields.HISTORY,
            key: ClientJobFormFields.HISTORY,
            width: 80,
            render: (_, record) => (
                <div role="button">
                    <FaHistory
                        className="fs-5"
                        onClick={() => {
                            setSelectedJob(record);
                            setIsHistoryModalOpen(true);
                        }}
                    />
                </div>
            ),
        },
    ];

    const fetchJobs = async (
        page: number,
        pageSize: number,
        search: string
    ) => {
        setJobsData((prev) => ({ ...prev, loading: true }));
        try {
            const result = await getClientJobs(
                client.id,
                page,
                pageSize,
                search
            );
            setJobsData((prev) => ({
                ...prev,
                jobs: result.jobs,
                pagination: {
                    current: result.page,
                    pageSize: result.pageSize,
                    total: result.total,
                },
                loading: false,
            }));
        } catch (error: any) {
            console.error("Error fetching jobs:", error);
            showToast("danger", error.message || "Failed to fetch jobs");
            Sentry.captureException(error);
            setJobsData((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleSearch = (value: string) => {
        setJobsData((prev) => ({
            ...prev,
            searchQuery: value,
            pagination: { ...prev.pagination, current: 1 },
        }));
        fetchJobs(FIRST_PAGE, jobsData.pagination.pageSize, value);
    };

    const debouncedSearch = useMemo(
        () => debounce(handleSearch, DEBOUNCE_DELAY),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        fetchJobs(FIRST_PAGE, DEFAULT_PAGE_SIZE, "");
    }, [client.id]);

    return (
        <div>
            {!addJobDisplay ? (
                <>
                    <Row className="">
                        <Col span={12}>
                            <h5>
                                All Jobs{" "}
                                <span className="text-muted">
                                    ({jobsData.pagination.total || 0})
                                </span>
                            </h5>
                        </Col>
                        <Col span={12} className="text-end">
                            <Input
                                placeholder="Search jobs..."
                                className="border"
                                style={{ width: "40%" }}
                                defaultValue={jobsData.searchQuery}
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    debouncedSearch(value);
                                }}
                                allowClear
                            />
                            {canCreateClient && (
                                <Button
                                    type="primary"
                                    className="ms-3"
                                    onClick={() => {
                                        setAddJobDisplay(true);
                                    }}
                                >
                                    Add Job
                                </Button>
                            )}
                        </Col>
                    </Row>

                    <div className="mt-4">
                        <Table
                            columns={columns}
                            dataSource={jobsData.jobs}
                            rowKey="id"
                            loading={jobsData.loading}
                            pagination={{
                                current: jobsData.pagination.current,
                                pageSize: jobsData.pagination.pageSize,
                                total: jobsData.pagination.total,
                                onChange: (page, pageSize) => {
                                    fetchJobs(
                                        page,
                                        pageSize,
                                        jobsData.searchQuery
                                    );
                                },
                                onShowSizeChange: (current, size) => {
                                    setJobsData((prev) => ({
                                        ...prev,
                                        pagination: {
                                            ...prev.pagination,
                                            current: 1,
                                            pageSize: size,
                                        },
                                    }));
                                    fetchJobs(
                                        FIRST_PAGE,
                                        size,
                                        jobsData.searchQuery
                                    );
                                },
                                showSizeChanger: true,
                                pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                            }}
                            scroll={{ x: 1500, y: "calc(100vh - 350px)" }}
                            className="min-w-100 overflow-auto bg-white rounded"
                        />
                    </div>
                </>
            ) : (
                <ClientJobForm
                    client={client}
                    editData={editJobData}
                    onSuccess={() => {
                        setAddJobDisplay(false);
                        setEditJobData(null);
                        setJobsData({
                            ...INITIAL_JOBS_DATA,
                            pagination: {
                                ...INITIAL_JOBS_DATA.pagination,
                                pageSize: jobsData.pagination.pageSize,
                            },
                        });
                        fetchJobs(
                            FIRST_PAGE,
                            jobsData.pagination.pageSize,
                            jobsData.searchQuery
                        );
                    }}
                    onCancel={() => {
                        setAddJobDisplay(false);
                        setEditJobData(null);
                    }}
                />
            )}
            <ClientJobHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={() => {
                    setIsHistoryModalOpen(false);
                    setSelectedJob(null);
                }}
                job={selectedJob}
            />
        </div>
    );
};

export default ClientJobs;
